import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, Search } from 'lucide-react'
import { toast } from 'sonner'
import { OfflineItemSearchModal, ItemSearchResult } from './OfflineItemSearchModal'
import { recalculateItemPrice } from '@/app/lib/pricingUtils'

// Define the structure for grid columns
export interface GridColumn {
  field: string // Key in the data object
  header: string // Display name in the table header
  width?: string // Optional width (e.g., '150px')
  type?: 'text' | 'number' | 'price' | 'product' | 'actions' // Cell type for rendering/editing
  editable?: boolean // Whether the cell is editable (defaults to true if not 'actions' or 'product')
  render?: (row: Record<string, unknown>) => React.ReactNode // Custom render function (optional)
}

// Alias ItemSearchResult as ProductSearchResult for consistency within this component context
export type ProductSearchResult = ItemSearchResult

// Props for the EditableDataGrid component
interface EditableDataGridProps {
  columns: GridColumn[] // Column definitions
  data: Record<string, unknown>[] // Array of data objects for rows
  setData: React.Dispatch<React.SetStateAction<Record<string, unknown>[]>> // Function to update the data state
  productSearchFn?: (searchTerm: string) => Promise<ProductSearchResult[]> // Async function to search for products
  isLoadingProducts?: boolean // Loading state for product search
  disabled?: boolean // Disables editing and actions
  onProductSelect?: (product: ProductSearchResult, rowIndex: number) => Promise<void> | void // Callback when a product is selected (can be async)
  onChange?: (data: Record<string, unknown>[]) => void // Optional onChange callback
  siteId?: number // Added siteId property for tax calculations
  onQuantityChangeForRow?: (rowIndex: number, newQuantity: number) => Promise<void> // NEW: Callback when quantity changes for a row with applied rule
}

// Define an interface for row data to help with type checking
interface GridRowData extends Record<string, unknown> {
  item_id?: number | string
  rate?: number | string
  quantity?: number | string
  discount_percent?: number | string
  discount_amount?: number | string
  amount?: number | string
  tax_rate_id?: number | string | null
  applied_sale_rule_id?: number | null
  item_name?: string
  item_code?: string
  description?: string
  taxable_amount?: number
  tax_amount?: number
}

export const OfflineEditableDataGrid: React.FC<EditableDataGridProps> = ({
  columns,
  data,
  setData,
  productSearchFn,
  isLoadingProducts = false,
  disabled = false,
  onProductSelect,
  onChange,
  siteId,
  onQuantityChangeForRow
}) => {
  // State for tracking the currently focused/active cell
  const [activeCell, setActiveCell] = useState<{ row: number; col: string; field: string } | null>(
    null
  )
  // State for controlling the item search modal
  const [isItemModalOpen, setIsItemModalOpen] = useState(false)
  // State to store the index of the row targeted by the modal
  const [modalTargetRowIndex, setModalTargetRowIndex] = useState<number | null>(null)
  // Ref to store the element that triggered the item search modal (for focus return)
  const modalTriggerRef = useRef<HTMLElement | null>(null)
  // Ref to store references to all input/interactive elements in the grid cells
  const inputRefs = useRef<(HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null)[][]>([])

  // Log data changes for debugging purposes
  useEffect(() => {
    console.log('[EditableDataGrid] Grid data updated:', data)
  }, [data])

  // Function to programmatically focus a specific cell
  const focusCell = useCallback(
    (rowIndex: number, field: string): void => {
      console.log(`[EditableDataGrid] Requesting focus for cell: row ${rowIndex}, field ${field}`)
      // Use requestAnimationFrame to ensure focus happens after DOM updates
      requestAnimationFrame(() => {
        try {
          // Validate row index
          if (rowIndex < 0 || rowIndex >= inputRefs.current.length) {
            console.warn(`[EditableDataGrid] Invalid row index for focus: ${rowIndex}`)
            return
          }

          // Find the column index for the given field
          const colIndex = columns.findIndex((c) => c.field === field)
          if (colIndex < 0) {
            console.warn(`[EditableDataGrid] Field not found for focus: ${field}`)
            return
          }

          // Ensure the refs array for the row exists
          if (!inputRefs.current[rowIndex]) {
            inputRefs.current[rowIndex] = []
          }

          // Get the DOM element reference
          const inputElement = inputRefs.current[rowIndex]?.[colIndex]

          if (inputElement) {
            console.log(`[EditableDataGrid] Focusing element:`, inputElement)
            // Set the active cell state *before* focusing
            setActiveCell({ row: rowIndex, col: colIndex.toString(), field })
            // Focus the element
            inputElement.focus()
            // Select text content if it's an input or textarea
            if (
              inputElement instanceof HTMLInputElement ||
              inputElement instanceof HTMLTextAreaElement
            ) {
              inputElement.select()
            }
          } else {
            // Log if the element reference wasn't found (might happen during rapid updates)
            console.log(
              `[EditableDataGrid] Input element ref not found for row ${rowIndex}, column ${colIndex} (field: ${field})`
            )
          }
        } catch (error) {
          console.error('[EditableDataGrid] Error focusing cell:', error)
        }
      })
    },
    [columns]
  ) // Dependency: columns array

  // Helper function to calculate discount amount for a row
  const calculateDiscountAmount = (row: Record<string, unknown>): number => {
    const quantity = parseFloat(String(row.quantity || 0))
    const rate = parseFloat(String(row.rate || 0))
    const discountPercent = parseFloat(String(row.discount_percent || 0))
    const grossAmount = quantity * rate
    const discountAmount = (grossAmount * discountPercent) / 100
    // Round to 2 decimal places
    return Math.round(discountAmount * 100) / 100
  }

  // Helper function to calculate the final amount for a row (after discount)
  const calculateFinalRowAmount = (row: Record<string, unknown>): number => {
    const quantity = parseFloat(String(row.quantity || 0))
    const rate = parseFloat(String(row.rate || 0))
    const grossAmount = quantity * rate
    const discountAmount = calculateDiscountAmount(row)
    const finalAmount = grossAmount - discountAmount
    // Round to 2 decimal places
    return Math.round(finalAmount * 100) / 100
  }

  // Function to determine the coordinates (row, field) of the next/previous editable cell
  const getNextCellCoordinates = useCallback(
    (
      currentRow: number,
      currentField: string,
      direction: 'next' | 'prev'
    ): { row: number; field: string } | null => {
      // Get a list of fields corresponding to editable columns
      const editableColumns = columns
        .filter((col) => col.type !== 'actions' && col.editable !== false)
        .map((col) => col.field)

      if (editableColumns.length === 0) return null // No editable columns

      const currentColIndex = editableColumns.indexOf(currentField)

      // Handle case where the current field itself isn't directly editable (e.g., product cell div)
      if (currentColIndex === -1) {
        const currentActualIndex = columns.findIndex((c) => c.field === currentField)
        if (direction === 'next') {
          // Find the next editable column in the same row
          for (let i = currentActualIndex + 1; i < columns.length; i++) {
            if (editableColumns.includes(columns[i].field)) {
              return { row: currentRow, field: columns[i].field }
            }
          }
          // If no more editable in this row, wrap to the first editable cell of the next row
          if (currentRow < data.length - 1)
            return { row: currentRow + 1, field: editableColumns[0] }
        } else {
          // direction 'prev'
          // Find the previous editable column in the same row
          for (let i = currentActualIndex - 1; i >= 0; i--) {
            if (editableColumns.includes(columns[i].field)) {
              return { row: currentRow, field: columns[i].field }
            }
          }
          // If no more editable before this in the row, wrap to the last editable cell of the previous row
          if (currentRow > 0)
            return { row: currentRow - 1, field: editableColumns[editableColumns.length - 1] }
        }
        return null // No editable field found
      }

      // Standard logic for navigating between editable fields
      if (direction === 'next') {
        // Move to the next editable column in the same row
        if (currentColIndex < editableColumns.length - 1) {
          return { row: currentRow, field: editableColumns[currentColIndex + 1] }
        }
        // Move to the first editable column of the next row
        if (currentRow < data.length - 1) {
          return { row: currentRow + 1, field: editableColumns[0] }
        }
        return null // Reached the end of the grid
      } else {
        // direction 'prev'
        // Move to the previous editable column in the same row
        if (currentColIndex > 0) {
          return { row: currentRow, field: editableColumns[currentColIndex - 1] }
        }
        // Move to the last editable column of the previous row
        if (currentRow > 0) {
          return { row: currentRow - 1, field: editableColumns[editableColumns.length - 1] }
        }
      }
      return null // Reached the beginning of the grid
    },
    [columns, data.length]
  ) // Dependencies: columns and data length

  // Function to add a new empty row to the grid
  const handleAddRow = useCallback((): void => {
    console.log('[EditableDataGrid] handleAddRow started.')
    // Create a new row object with default values based on column types
    const newRow: Record<string, unknown> = {}
    columns.forEach((column) => {
      if (column.type === 'number' || column.type === 'price') {
        newRow[column.field] = 0
      } else if (column.field === 'item_id') {
        newRow[column.field] = undefined // Ensure product cell shows placeholder initially
      } else {
        newRow[column.field] = ''
      }
      // Set specific defaults for common fields
      if (column.field === 'quantity') newRow[column.field] = 1
      if (column.field === 'discount_percent') newRow[column.field] = 0
      if (column.field === 'discount_amount') newRow[column.field] = 0
      if (column.field === 'amount') newRow[column.field] = 0
    })

    // Update the data state
    const newData = [...data, newRow]
    setData(newData)

    // Focus the first editable cell of the new row after render
    requestAnimationFrame(() => {
      const newRowIndex = newData.length - 1
      // Prioritize focusing the product cell if it exists
      const productField = columns.find((c) => c.type === 'product')?.field
      // Otherwise, find the first editable field
      const firstEditableField =
        productField || columns.find((c) => c.type !== 'actions' && c.editable !== false)?.field

      if (firstEditableField) {
        focusCell(newRowIndex, firstEditableField)
      }
    })
  }, [columns, data, setData, focusCell]) // Dependencies

  // Function to delete a row from the grid
  const handleDeleteRow = useCallback(
    (rowIndex: number): void => {
      // Prevent deleting the last row
      if (data.length === 1) {
        toast.warning('Cannot delete the last row. At least one item is required.')
        return
      }

      // Filter out the row to be deleted
      const newData = data.filter((_, index) => index !== rowIndex)
      setData(newData)

      // If the deleted row was the last one, focus the first editable cell of the now-last row
      if (rowIndex === data.length - 1 && rowIndex > 0) {
        const editableColumns = columns
          .filter((col) => col.type !== 'actions' && col.editable !== false)
          .map((col) => col.field)
        if (editableColumns.length > 0) {
          const prevRowIndex = rowIndex - 1
          const fieldToFocus = editableColumns[0] // Focus the first editable field
          // Use a short timeout as focus might need the element to be fully settled after state update
          setTimeout(() => focusCell(prevRowIndex, fieldToFocus), 50)
        }
      }
      // Clear the active cell state as the focused row might be gone
      setActiveCell(null)
    },
    [data, setData, columns, focusCell]
  ) // Dependencies

  // Function to handle item selection from the search modal
  const handleSelectItem = useCallback(
    async (item: ProductSearchResult): Promise<void> => {
      if (modalTargetRowIndex === null) return // Exit if no target row is set
      const rowIndex = modalTargetRowIndex
      console.log(`[EditableDataGrid] Item selected for row ${rowIndex}:`, item)

      // --- Stage 1: Update Grid State & Close Modal ---
      // Update the grid's data state with basic item info (name, code, etc.)
      setData((currentData) => {
        const newData = [...currentData]
        if (newData[rowIndex]) {
          // Create the updated row object with proper typing
          const updatedRow: GridRowData = {
            ...newData[rowIndex], // Keep existing values like godown_id if present
            item_id: item.id,
            description: item.description || item.name, // Use description or name
            item_name: item.name,
            item_code: item.code || '',
            rate: '', // Clear rate, to be updated by parent or mock
            // Keep existing quantity/discount or use defaults if they weren't set
            quantity:
              typeof newData[rowIndex].quantity === 'number' ||
              typeof newData[rowIndex].quantity === 'string'
                ? newData[rowIndex].quantity
                : 1,
            discount_percent:
              typeof newData[rowIndex].discount_percent === 'number' ||
              typeof newData[rowIndex].discount_percent === 'string'
                ? newData[rowIndex].discount_percent
                : 0,
            tax_rate_id: item.tax_rate_id || null, // Get tax_rate_id from the item if available
            // Reset calculated fields; they depend on the rate
            discount_amount: 0,
            taxable_amount: 0,
            tax_amount: 0,
            amount: 0
          }

          newData[rowIndex] = updatedRow
        }
        return newData // Return the updated data array
      })

      // Close the modal *after* the initial state update is triggered
      setIsItemModalOpen(false)
      setModalTargetRowIndex(null) // Reset target row
      modalTriggerRef.current = null // Clear the trigger ref, as selection handles focus now

      // --- Stage 2: External Action (e.g., Fetch Price) ---
      // Call the optional onProductSelect prop provided by the parent component
      try {
        if (onProductSelect) {
          console.log(`[EditableDataGrid] Calling onProductSelect for row ${rowIndex}`)
          // Use await in case the parent function is async (e.g., fetching price)
          await onProductSelect(item, rowIndex)
          console.log(`[EditableDataGrid] onProductSelect finished for row ${rowIndex}`)
          // The parent component (SalesInvoiceForm) is expected to update
          // the rate/amounts by calling `setData` if necessary after fetching.
        } else {
          // Fallback: If no onProductSelect provided, use a mock price
          console.log(
            `[EditableDataGrid] No onProductSelect, using mock price lookup for row ${rowIndex}`
          )
          const mockPrice = Math.round(Math.random() * 100 * 100) / 100 // Random price
          setData((currentData) => {
            const newData = [...currentData]
            if (newData[rowIndex]) {
              // Create a new row object with the mock price
              const rowData = newData[rowIndex] as GridRowData
              const updatedRow: GridRowData = {
                ...rowData,
                rate: mockPrice
              }

              // Calculate derived values and ensure proper type safety
              const discountAmount = calculateDiscountAmount(updatedRow)
              const finalAmount = calculateFinalRowAmount(updatedRow)

              updatedRow.discount_amount = discountAmount
              updatedRow.amount = finalAmount

              newData[rowIndex] = updatedRow // Put the updated row back into the array
            }
            return newData // Return the modified data array
          })
          console.log(`[EditableDataGrid] Mock price ${mockPrice} set for row ${rowIndex}`)
        }
      } catch (error) {
        // Handle errors during the external action or price update
        console.error(
          `[EditableDataGrid] Error during onProductSelect or price update for row ${rowIndex}:`,
          error
        )
        toast.error('Error updating item details after selection.')
      }

      // --- Stage 3: Focus Next Field ---
      // After all updates (state and potential async actions) are likely settled, focus 'quantity'
      console.log(
        `[EditableDataGrid] Attempting to focus 'quantity' field for row ${rowIndex} after item selection.`
      )
      focusCell(rowIndex, 'quantity')
    },
    [
      modalTargetRowIndex,
      setData,
      onProductSelect,
      focusCell,
      calculateDiscountAmount,
      calculateFinalRowAmount
    ]
  )

  // Callback function to handle the modal's open/close state changes
  const handleModalOpenChange = useCallback((isOpen: boolean) => {
    console.log('[EditableDataGrid] Modal open change:', isOpen)
    setIsItemModalOpen(isOpen) // Update the modal visibility state

    if (!isOpen) {
      // Modal is closing
      // Check if the trigger ref still exists (meaning selection didn't clear it)
      if (modalTriggerRef.current) {
        console.log(
          '[EditableDataGrid] Modal closed (likely via Escape/Outside Click), focusing trigger:',
          modalTriggerRef.current
        )
        // Focus the element that originally opened the modal
        // Use a timeout to ensure focus occurs after the modal is fully removed from DOM/transitioned out
        setTimeout(() => {
          modalTriggerRef.current?.focus()
          // If the trigger was an input, select its text
          if (
            modalTriggerRef.current instanceof HTMLInputElement ||
            modalTriggerRef.current instanceof HTMLTextAreaElement
          ) {
            modalTriggerRef.current.select()
          }
          // We could try to find the row/col index from the ref to set activeCell,
          // but just focusing the trigger element is usually sufficient for Escape.
        }, 50) // 50ms delay seems reasonable
      }
      // Always clear the target index and trigger ref when the modal closes
      setModalTargetRowIndex(null)
      modalTriggerRef.current = null
    }
  }, []) // No dependencies needed for this callback

  // Function to handle changes in input cell values
  const handleCellChange = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex: number,
    field: string
  ): Promise<void> => {
    console.log(
      `[EditableDataGrid] handleCellChange - field: ${field}, rowIndex: ${rowIndex}, value: ${e.target.value}`
    )

    const value = e.target.value
    const newData = [...data]
    if (!newData[rowIndex]) return

    const column = columns.find((c) => c.field === field)
    let processedValue: string | number = value

    // For number/price fields, store raw input value first, only convert to number if valid
    if (column?.type === 'number' || column?.type === 'price') {
      // Store as string for empty, '-', or partial valid inputs like '1.' to allow user to keep typing
      if (value === '' || value === '-' || value === '.' || value.endsWith('.')) {
        processedValue = value
        console.log(`[EditableDataGrid] Keeping raw input value: "${value}" for field ${field}`)
      } else {
        // Parse only if it's a complete, valid number
        const floatValue = parseFloat(value)
        // If parsing succeeds, store as number, otherwise keep existing value
        if (!isNaN(floatValue)) {
          processedValue = floatValue
          console.log(`[EditableDataGrid] Parsed numeric value: ${floatValue} for field ${field}`)
        } else {
          // For invalid input, keep raw string to allow continued editing
          processedValue = value
          console.log(
            `[EditableDataGrid] Keeping invalid input as-is for editing: "${value}" for field ${field}`
          )
        }
      }
    }

    // Create the updated row object as GridRowData type for better type safety
    const updatedRow: GridRowData = {
      ...newData[rowIndex],
      [field]: processedValue
    }

    // ***MODIFIED LOGIC FOR RATE RECALCULATION***
    if (field === 'quantity' && typeof onQuantityChangeForRow === 'function') {
      console.log(
        `[EditableDataGrid] Quantity changed for row ${rowIndex}. Calling onQuantityChangeForRow.`
      )
      // Always call back to parent to re-fetch price details when quantity changes
      // The parent (SalesInvoiceForm) will then call getFinalItemPriceDetails and update the whole row
      // Ensure 'processedValue' is a valid number before passing
      const newQuantity = parseFloat(String(processedValue))
      if (!isNaN(newQuantity)) {
        await onQuantityChangeForRow(rowIndex, newQuantity) // Parent updates the row
        // Parent will handle the full update, so we can exit early
        return
      } else {
        console.warn(
          `[EditableDataGrid] Invalid quantity value for recalculation: ${processedValue}`
        )
      }
    }
    // Standard recalculation if no rule-based quantity change
    else if (['rate', 'discount_percent'].includes(field) && typeof processedValue === 'number') {
      console.log(
        `[EditableDataGrid] Recalculating values for row ${rowIndex} due to ${field} change to ${processedValue}`
      )

      // Get the item_id and tax_rate_id for recalculation
      const itemId = Number(updatedRow.item_id) || undefined
      const taxRateId = Number(updatedRow.tax_rate_id) || undefined

      // Extract values for calculation, defaulting partial inputs to 0 for calculation
      const quantity =
        typeof updatedRow.quantity === 'number'
          ? updatedRow.quantity
          : parseFloat(String(updatedRow.quantity || 0))
      const rate =
        typeof updatedRow.rate === 'number'
          ? updatedRow.rate
          : parseFloat(String(updatedRow.rate || 0))
      const discountPercent =
        typeof updatedRow.discount_percent === 'number'
          ? updatedRow.discount_percent
          : parseFloat(String(updatedRow.discount_percent || 0))

      // Only recalculate if we have valid numbers
      if (!isNaN(quantity) && !isNaN(rate) && !isNaN(discountPercent)) {
        // Use our utility to calculate taxes
        console.log(
          `[EditableDataGrid] Calling recalculateItemPrice with: itemId=${itemId}, siteId=${siteId}, taxRateId=${taxRateId}, quantity=${quantity}, rate=${rate}, discountPercent=${discountPercent}`
        )

        const { taxableAmount, taxAmount, totalAmount } = recalculateItemPrice(
          itemId,
          taxRateId,
          quantity,
          rate,
          discountPercent
        )

        console.log(
          `[EditableDataGrid] Calculation results: taxableAmount=${taxableAmount}, taxAmount=${taxAmount}, totalAmount=${totalAmount}`
        )

        // Update row with calculated values
        updatedRow.taxable_amount = taxableAmount
        updatedRow.tax_amount = taxAmount
        updatedRow.amount = totalAmount
      } else {
        console.log(
          `[EditableDataGrid] Skipping calculation due to invalid numbers: quantity=${quantity}, rate=${rate}, discountPercent=${discountPercent}`
        )
      }
    } else if (
      ['quantity', 'rate', 'discount_percent'].includes(field) &&
      (value === '' || value === '-' || value === '.')
    ) {
      // For empty/partial inputs in calculation fields, keep existing calculated values
      console.log(`[EditableDataGrid] Partial input detected, keeping existing calculated values`)
    }

    // Update data and trigger onChange
    newData[rowIndex] = updatedRow
    console.log(`[EditableDataGrid] Setting new data for row ${rowIndex}:`, updatedRow)
    setData(newData)
    if (onChange) {
      onChange(newData)
    }
  }

  // Function to handle the blur event on input cells (finalize number parsing)
  const handleCellBlur = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex: number,
    field: string
  ): Promise<void> => {
    console.log(
      `[EditableDataGrid] handleCellBlur - field: ${field}, rowIndex: ${rowIndex}, value: ${e.target.value}`
    )

    const column = columns.find((c) => c.field === field)
    const currentDataRow = data[rowIndex]

    if (!currentDataRow) {
      console.warn(`[EditableDataGrid] No data row found at index ${rowIndex}`)
      return
    }

    // Get the current value from the data row
    const currentValue = currentDataRow[field]
    console.log(
      `[EditableDataGrid] Current value for ${field}: ${currentValue} (type: ${typeof currentValue})`
    )

    // Create a new data array for the update
    const newData = [...data]
    let parsedValue = currentValue
    let needsUpdate = false

    // Finalize parsing only for number/price fields
    if (column?.type === 'number' || column?.type === 'price') {
      // If the user left empty, partial input, or invalid numeric string, convert to 0
      if (
        currentValue === '' ||
        currentValue === '-' ||
        currentValue === '.' ||
        (typeof currentValue === 'string' && isNaN(parseFloat(currentValue)))
      ) {
        parsedValue = 0
        needsUpdate = true
        console.log(`[EditableDataGrid] Converted empty/invalid input to 0 for ${field}`)
      }
      // If it's already a string that can be parsed to a number, convert it
      else if (typeof currentValue === 'string' && !isNaN(parseFloat(currentValue))) {
        parsedValue = parseFloat(currentValue)
        needsUpdate = true
        console.log(
          `[EditableDataGrid] Converted string "${currentValue}" to number ${parsedValue} for ${field}`
        )
      }
      // No need to update if it's already a number
    }

    if (needsUpdate) {
      const updatedRow = { ...currentDataRow, [field]: parsedValue }
      console.log(`[EditableDataGrid] Updated row on blur: ${field} set to ${parsedValue}`)
      newData[rowIndex] = updatedRow
      setData(newData)
    }

    // After parsing, if it's a quantity field and a rule was applied:
    if (field === 'quantity' && typeof onQuantityChangeForRow === 'function') {
      const finalizedQuantity = parseFloat(String(data[rowIndex][field] || 0))
      if (!isNaN(finalizedQuantity)) {
        console.log(
          `[EditableDataGrid] Quantity blurred for row ${rowIndex}. Calling onQuantityChangeForRow.`
        )
        await onQuantityChangeForRow(rowIndex, finalizedQuantity)
        return // Parent handles the full update
      }
    } else if (['rate', 'discount_percent'].includes(field)) {
      // Standard recalculation on blur
      const currentDataRow = data[rowIndex]
      if (!currentDataRow) return
      const updatedRow = { ...currentDataRow }
      const itemId = Number(updatedRow.item_id) || undefined
      const taxRateId = Number(updatedRow.tax_rate_id) || undefined
      const quantity = parseFloat(String(updatedRow.quantity || 0)) // Ensure parsed from current state
      const rate = parseFloat(String(updatedRow.rate || 0))
      const discountPercent = parseFloat(String(updatedRow.discount_percent || 0))

      if (!isNaN(quantity) && !isNaN(rate) && !isNaN(discountPercent)) {
        const { taxableAmount, taxAmount, totalAmount } = recalculateItemPrice(
          itemId,
          taxRateId,
          quantity,
          rate,
          discountPercent
        )
        // Create a new row object for setData to ensure re-render
        const finalUpdatedRow = {
          ...updatedRow,
          quantity, // ensure parsed value
          rate, // ensure parsed value
          discount_percent: discountPercent, // ensure parsed value - FIX: using discountPercent variable
          taxable_amount: taxableAmount,
          tax_amount: taxAmount,
          amount: totalAmount
        }
        const finalNewData = [...data]
        finalNewData[rowIndex] = finalUpdatedRow
        setData(finalNewData)
        if (onChange) {
          onChange(finalNewData)
        }
      }
    }
    // Ensure activeCell is cleared if focus is lost from grid.
    // setActiveCell(null); // This might be too aggressive here. Handled by onFocus of other elements.
  }

  // Function to handle keyboard events within grid cells
  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>,
      rowIndex: number,
      field: string
    ): void => {
      const isProductCell = columns.find((c) => c.field === field)?.type === 'product'
      const key = e.key

      // Example: Prevent default browser action for Ctrl+S if needed
      // if (e.ctrlKey && key === 's') e.preventDefault();

      switch (key) {
        case 'Enter':
          e.preventDefault()
          if (isProductCell) {
            // Store trigger ref and open modal for product cell
            modalTriggerRef.current = e.currentTarget as HTMLElement
            setModalTargetRowIndex(rowIndex)
            setIsItemModalOpen(true)
          } else {
            // Move to the next logical cell (could wrap to next row or add new row)
            const next = getNextCellCoordinates(rowIndex, field, 'next')
            if (next) {
              focusCell(next.row, next.field)
            } else {
              // If Enter pressed on the very last editable cell, add a new row
              handleAddRow()
            }
          }
          break

        case 'Tab': {
          e.preventDefault()
          const direction = e.shiftKey ? 'prev' : 'next'
          const next = getNextCellCoordinates(rowIndex, field, direction)
          if (next) {
            // Move focus to the next/previous cell
            focusCell(next.row, next.field)
          } else if (direction === 'next') {
            // If Tab pressed on last cell, add a new row
            handleAddRow()
          } else {
            // If Shift+Tab pressed on first cell, allow focus to leave the grid
            ;(e.target as HTMLElement).blur()
          }
          break
        }

        case 'ArrowUp':
          if (rowIndex > 0) {
            e.preventDefault()
            // Move focus to the same field in the previous row
            focusCell(rowIndex - 1, field)
          }
          break

        case 'ArrowDown':
          e.preventDefault()
          if (rowIndex < data.length - 1) {
            // Move focus to the same field in the next row
            focusCell(rowIndex + 1, field)
          } else {
            // If ArrowDown pressed on the last row, add a new row
            handleAddRow()
          }
          break

        case 'Delete':
        case 'Backspace':
          // Use Ctrl/Meta + Delete/Backspace to delete the current row
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            handleDeleteRow(rowIndex)
          }
          // Allow normal backspace/delete within input fields otherwise
          break

        case 'F2':
          // Open product search modal if F2 pressed on a product cell
          if (isProductCell) {
            e.preventDefault()
            modalTriggerRef.current = e.currentTarget as HTMLElement // Store trigger
            setModalTargetRowIndex(rowIndex)
            setIsItemModalOpen(true)
          }
          break

        case 'Escape':
          // Blur the current cell and clear active state on Escape
          ;(e.target as HTMLElement).blur()
          setActiveCell(null)
          break

        default:
          // Allow default behavior for other keys
          break
      }
    },
    [columns, data.length, focusCell, handleAddRow, handleDeleteRow, getNextCellCoordinates]
  ) // Dependencies

  // --- Render Logic ---
  return (
    // Container with horizontal scroll if needed
    <div className="w-full overflow-x-auto border rounded-md bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            {/* Render table headers */}
            {columns.map((column) => (
              <TableHead
                key={column.field}
                // Apply width and minimum width for better layout control
                style={{ width: column.width, minWidth: column.width }}
                // Adjust padding, prevent wrapping
                className="px-2 py-2 font-medium text-sm whitespace-nowrap bg-muted/50"
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Render table rows */}
          {data.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              // Highlight the row containing the active cell
              className={activeCell?.row === rowIndex ? 'bg-muted/50' : ''}
            >
              {/* Render cells for the current row */}
              {columns.map((column, colIndex) => {
                const cellKey = `${rowIndex}-${column.field}` // Unique key for the cell
                // Check if this cell is the currently active one
                const isActive = activeCell?.row === rowIndex && activeCell?.field === column.field
                // Function to assign the ref to the correct element in the inputRefs array
                const elementRef = (
                  el: HTMLInputElement | HTMLTextAreaElement | HTMLDivElement | null
                ): void => {
                  if (!inputRefs.current[rowIndex]) {
                    inputRefs.current[rowIndex] = [] // Ensure row array exists
                  }
                  inputRefs.current[rowIndex][colIndex] = el // Store the element ref
                }

                // --- Cell Type Rendering ---

                // 1. Actions Cell (Delete Button)
                if (column.type === 'actions') {
                  return (
                    <TableCell key={cellKey} className="px-1 py-1 text-center">
                      {' '}
                      {/* Minimal padding */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive" // Smaller icon button
                        onClick={() => handleDeleteRow(rowIndex)}
                        // Disable delete for the last row or if grid is disabled
                        disabled={disabled || data.length <= 1}
                        type="button" // Prevent form submission
                        tabIndex={-1} // Remove from default tab order
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )
                }

                // 2. Product Cell (Clickable Div for Modal)
                if (column.type === 'product') {
                  return (
                    <TableCell key={cellKey} className="px-2 py-1 align-middle">
                      {' '}
                      {/* Standard padding */}
                      <div
                        ref={elementRef as React.Ref<HTMLDivElement>} // Assign ref
                        // Styling: make it look interactive, highlight when active
                        className={`flex items-center h-8 px-3 border rounded-md cursor-pointer transition-colors hover:bg-muted/80 ${isActive ? 'ring-2 ring-ring ring-offset-background ring-offset-2' : 'border-input'} bg-background`}
                        // Open modal on click
                        onClick={(e) => {
                          if (disabled) return
                          modalTriggerRef.current = e.currentTarget // Store trigger ref
                          setModalTargetRowIndex(rowIndex)
                          setIsItemModalOpen(true)
                        }}
                        // Handle keyboard events (Enter, F2, navigation)
                        onKeyDown={(e) => !disabled && handleKeyDown(e, rowIndex, column.field)}
                        tabIndex={disabled ? -1 : 0} // Make focusable unless disabled
                        role="button"
                        aria-label="Select product"
                        // Set active cell state on focus
                        onFocus={() =>
                          !disabled &&
                          setActiveCell({
                            row: rowIndex,
                            col: colIndex.toString(),
                            field: column.field
                          })
                        }
                      >
                        {/* Display selected item name or placeholder */}
                        <span className="flex-grow truncate text-sm">
                          {row.item_name ? (
                            String(row.item_name)
                          ) : (
                            <span className="text-muted-foreground italic">Select...</span>
                          )}
                        </span>
                        {/* Search icon */}
                        <Search className="h-4 w-4 ml-2 opacity-50 flex-shrink-0" />
                      </div>
                    </TableCell>
                  )
                }

                // 3. Read-only Calculated Cells (e.g., Amount, Discount Amount)
                if (column.editable === false) {
                  // Get display value, default to empty string
                  let displayValue = String(row[column.field] ?? '')
                  // Format price types to 2 decimal places
                  if (column.type === 'price') {
                    const numericValue = parseFloat(displayValue)
                    // Default to '0.00' if not a valid number
                    displayValue = isNaN(numericValue) ? '0.00' : numericValue.toFixed(2)
                  } else if (column.type === 'number') {
                    // Add formatting for non-price numbers if needed
                  }
                  return (
                    <TableCell
                      key={cellKey}
                      className={`px-2 py-1 text-sm align-middle ${column.type === 'price' || column.type === 'number' ? 'text-right' : ''}`}
                    >
                      {' '}
                      {/* Align numeric right */}
                      {displayValue}
                    </TableCell>
                  )
                }

                // 4. Default Editable Input Cell (Text, Number, Price)
                return (
                  <TableCell key={cellKey} className="px-2 py-1 align-middle">
                    {' '}
                    {/* Standard padding */}
                    <Input
                      ref={elementRef as React.Ref<HTMLInputElement>} // Assign ref
                      // Determine input type based on column definition
                      type={column.type === 'number' || column.type === 'price' ? 'text' : 'text'}
                      // Get value, default to empty string
                      value={String(row[column.field] ?? '')}
                      // Apply styling, highlight active cell, align numeric right
                      className={`h-8 text-sm bg-background ${isActive ? 'ring-2 ring-ring ring-offset-background ring-offset-2' : ''} ${column.type === 'number' || column.type === 'price' ? 'text-right' : ''}`}
                      // Attach event handlers
                      onChange={(e) => handleCellChange(e, rowIndex, column.field)}
                      onFocus={() =>
                        setActiveCell({
                          row: rowIndex,
                          col: colIndex.toString(),
                          field: column.field
                        })
                      }
                      onBlur={(e) => handleCellBlur(e, rowIndex, column.field)} // Handle blur for final parsing
                      onKeyDown={(e) => handleKeyDown(e, rowIndex, column.field)}
                      disabled={disabled} // Disable input if grid is disabled
                      // Add placeholder for numeric fields
                      placeholder={column.type === 'number' || column.type === 'price' ? '0' : ''}
                    />
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Footer section with Add Item button */}
      <div className="p-2 border-t mt-auto">
        <Button
          variant="outline"
          size="sm"
          className="flex items-center"
          onClick={handleAddRow}
          disabled={disabled} // Disable if grid is disabled
          type="button" // Prevent form submission
          tabIndex={-1} // Remove from default tab order
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>

      {/* Render the Item Search Modal conditionally */}
      {productSearchFn && (
        <OfflineItemSearchModal
          isOpen={isItemModalOpen}
          onOpenChange={handleModalOpenChange} // Use the callback that handles focus return
          onSelectItem={handleSelectItem} // Handles item selection logic
          searchItems={productSearchFn} // Pass down the search function
          isLoading={isLoadingProducts} // Pass down loading state
        />
      )}
    </div>
  )
}
